import { StatusCounterLead, LeadStatusCountsGrouped } from "@/types/lead.type";
import { ApiResponseType } from "@/common/helpers/api.helpers";

export interface ConvertedRentersResponse {
  converted: number;
  convertedSixtyAge: number;
  reactivations: number;
  activations: number;
  activationsByConsultant?: { activations: number; reactivations: number; technicalConsultant: string; }[]
}

export interface KPISlice {
  totalLeadsCount: number;
  leadsConvertRenter: number;
  leadsCountByStatus: StatusCounterLead;
  conversionRate: string;
  plansTotals: Record<string, number>;
  plansTooltips: Record<string, string>;
  leadsSixtyDaysAgo: Record<string, number>;
  convertedSixtyAge: number;
}

export interface PlansData {
  planTotals: Record<string, number>;
  planTooltips: Record<string, string>;
}

export type DashboardApiResponse = ApiResponseType<LeadStatusCountsGrouped>;

export type RentersPlansApiResponse = ApiResponseType<Array<{ _id: string; count: number }>>;

export type ConvertedRentersApiResponse = ApiResponseType<{
  filter: ConvertedRentersResponse;
  filterToday: ConvertedRentersResponse;
}>;

export class DataProcessorService {
  /**
   * Converte counts genérico para StatusCounterLead
   */
  static convertCountsToStatusCounter(counts: Record<string, number>): StatusCounterLead {
    return {
      new_lead: counts.new_lead ?? 0,
      incomplete: counts.incomplete ?? 0,
      ready_to_rent: counts.ready_to_rent ?? 0,
      converted: counts.converted ?? 0,
      archived: counts.archived ?? 0,
      convertedLeads: counts.convertedLeads ?? 0,
      under_negotiation: counts.under_negotiation ?? 0,
    };
  }

  /**
   * Cria um slice padrão de KPIs
   */
  static createDefaultSlice(overrides?: Partial<KPISlice>): KPISlice {
    const defaultSlice: KPISlice = {
      totalLeadsCount: 0,
      leadsConvertRenter: 0,
      leadsCountByStatus: {
        new_lead: 0,
        incomplete: 0,
        ready_to_rent: 0,
        converted: 0,
        archived: 0,
        convertedLeads: 0,
        under_negotiation: 0
      },
      leadsSixtyDaysAgo: {
        new_lead: 0,
        incomplete: 0,
        ready_to_rent: 0,
        converted: 0,
        archived: 0,
        convertedLeads: 0
      },
      convertedSixtyAge: 0,
      conversionRate: "0.00%",
      plansTotals: {},
      plansTooltips: {},
    };

    return { ...defaultSlice, ...overrides };
  }

  /**
   * Processa dados dos planos
   */
  static parsePlansData(data: { _id: string; count: number }[]): PlansData {
    const planTotals: Record<string, number> = {
      "Sua Vibe": 0,
      Anual: 0,
      Trimestral: 0,
      Plus: 0,
    };

    const planTooltips: Record<string, string> = {
      "Sua Vibe": "",
      Anual: "",
      Trimestral: "",
      Mensal: "",
      Plus: ""
    };

    data.forEach(({ _id, count }) => {
      const name = _id.toUpperCase();

      if (name === "SV") {
        planTotals["Sua Vibe"] += count;
      } else if (name === "ANUAL") {
        planTotals["Anual"] += count;
      } else if (name === "TRIMESTRAL") {
        planTotals["Trimestral"] += count;
      } else if (name === "PLUS") {
        planTotals["Plus"] += count;
      }
    });

			const totalPlans = Object.entries(planTotals)
			.filter(([key]) => key !== "Plus")
			.reduce((acc, [, value]) => acc + value, 0) || 1;

    Object.entries(planTotals).forEach(([category, count]) => {
      const percent = ((count / totalPlans) * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      planTooltips[category] = `Representando cerca de ${percent}% do total de planos da Locavibe`;

    });

    return { planTotals, planTooltips };
  }

  /**
   * Calcula taxa de conversão
   */
  static calculateConversionRate(converted: number, total: number): string {
    if (total === 0) return "0.00%";

    const rate = (converted / total) * 100;
    return `${rate.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }

  /**
   * Processa dados completos do dashboard
   */
  static processFullDashboardData(
    leadDashboard: DashboardApiResponse,
    rentersPlans: RentersPlansApiResponse,
    convertedRenters: ConvertedRentersApiResponse
  ) {
    // Processar planos
    const renterPlansCounts = Array.isArray(rentersPlans.data) ? rentersPlans.data : [];
    const { planTotals, planTooltips } = this.parsePlansData(renterPlansCounts);

    // Extrair dados de conversão
    const convertedFilter = convertedRenters.data?.filter?.converted ?? 0;
    const activFilter = convertedRenters.data?.filter?.activations ?? 0;
    const reacFilter = convertedRenters.data?.filter?.reactivations ?? 0;

    const convertedToday = convertedRenters.data?.filterToday?.converted ?? 0;
    const activToday = convertedRenters.data?.filterToday?.activations ?? 0;
    const reacToday = convertedRenters.data?.filterToday?.reactivations ?? 0;
    const activationsByConsultant = convertedRenters.data?.filter.activationsByConsultant ?? [];

    // Extrair dados do dashboard
    const dash = leadDashboard.data;
    const filterTotal: number = dash?.filter?.total ?? 0;
    const filterCounts: StatusCounterLead = this.convertCountsToStatusCounter(dash?.filter?.counts ?? {});
    const todayTotal: number = dash?.filterToday?.total ?? 0;
    const todayCounts: StatusCounterLead = this.convertCountsToStatusCounter(dash?.filterToday?.counts ?? {});
    const tma = dash?.tma ?? 0;
    const dailyGoal = dash?.dailyGoal ?? 40;
    const monthGoal = dash?.monthGoal ?? 1_000;
    const leadsByTime = dash?.leadsByTime ?? {};
    const leadsByOrigin = dash?.leadsByOrigin ?? [];
    const leadsByConsultant = dash?.leadsByConsultant ?? [];
    const leadsSixtyDaysAgo = dash?.leadsSixtyDaysAgo ?? [];
    const convertedSixtyAge = convertedRenters.data?.filter?.convertedSixtyAge ?? 0;
    const leadsByCompany = dash?.leadsByCompany ?? [];
    const leadsByCompanyFilter = dash?.leadsByCompanyFilter ?? [];

    // Calcular taxas de conversão
    const rateFilter = this.calculateConversionRate(convertedFilter, filterTotal);
    const rateToday = this.calculateConversionRate(convertedToday, todayTotal);

    // Criar slices
    const todaySlice = this.createDefaultSlice({
      totalLeadsCount: todayTotal,
      leadsConvertRenter: convertedToday,
      leadsCountByStatus: todayCounts,
      conversionRate: rateToday,
    });

    const filterSlice = this.createDefaultSlice({
      totalLeadsCount: filterTotal,
      leadsConvertRenter: convertedFilter,
      leadsCountByStatus: filterCounts,
      conversionRate: rateFilter,
      plansTotals: planTotals,
      plansTooltips: planTooltips,
      leadsSixtyDaysAgo: leadsSixtyDaysAgo,
      convertedSixtyAge: convertedSixtyAge
    });

    return {
      todaySlice,
      filterSlice,
      activationsFilter: activFilter,
      reactivationsFilter: reacFilter,
      activationsToday: activToday,
      reactivationsToday: reacToday,
      plansTotals: planTotals,
      plansTooltips: planTooltips,
      tma,
      dailyGoal, 
      monthGoal,
      leadsByTime,
      leadsByOrigin,
      leadsByConsultant,
      leadsSixtyDaysAgo,
      leadsByCompany,
      leadsByCompanyFilter,
      activationsByConsultant
    };
  }

  /**
   * Processa dados filtrados
   */
  static processFilteredData(
    dashRes: DashboardApiResponse,
    convFilterRes: ConvertedRentersApiResponse,
    convTodayRes: ConvertedRentersApiResponse,
    plansTotals: Record<string, number>,
    plansTooltips: Record<string, string>
  ) {
    const dash = dashRes.data;
    const filterTotal: number = dash?.filter?.total ?? 0;
    const filterCounts: StatusCounterLead = this.convertCountsToStatusCounter(dash?.filter?.counts ?? {});

    const convertedFilter: number = convFilterRes.data?.filter?.converted ?? 0;
    const rateFilter = this.calculateConversionRate(convertedFilter, filterTotal);

    const todayTotal: number = dash?.filterToday?.total ?? 0;
    const todayCounts: StatusCounterLead = this.convertCountsToStatusCounter(dash?.filterToday?.counts ?? {});

    const convertedToday: number = convTodayRes.data?.filter?.converted ?? 0;
    const rateToday = this.calculateConversionRate(convertedToday, todayTotal);

    const todaySlice = this.createDefaultSlice({
      totalLeadsCount: todayTotal,
      leadsConvertRenter: convertedToday,
      leadsCountByStatus: todayCounts,
      conversionRate: rateToday,
    });

    const filterSlice = this.createDefaultSlice({
      totalLeadsCount: filterTotal,
      leadsConvertRenter: convertedFilter,
      leadsCountByStatus: filterCounts,
      conversionRate: rateFilter,
      plansTotals,
      plansTooltips,
    });

    return {
      todaySlice,
      filterSlice,
    };
  }
}
